/**
 * Diálogo de Cuenta Corriente de Obra Social + Cobranza con retenciones.
 *
 * Tabs:
 *   1) Cuenta Corriente — saldo, movimientos, FCs pendientes
 *   2) Nuevo Recibo — emitir recibo de cobranza con retenciones
 *   3) Historial de recibos
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MoneyInput } from './MoneyInput';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Plus, Trash2, Loader2, Receipt, FileText, FileSpreadsheet, Printer, HandCoins } from 'lucide-react';
import { toast } from 'sonner';
import { fmtDate, todayLocalISO } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function InsurerCCDialog({ open, onOpenChange, insurer }) {
  const [tab, setTab] = useState('cc');
  const [cc, setCc] = useState(null);
  const [pending, setPending] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [whtTypes, setWhtTypes] = useState([]);
  const [cashBanks, setCashBanks] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [ccFrom, setCcFrom] = useState('');
  const [ccTo, setCcTo] = useState('');
  const [showAllCc, setShowAllCc] = useState(false); // v3.36.14 · false = sólo pendientes
  const [advDialog, setAdvDialog] = useState({ open: false, invoice_id: '', amount: '', saving: false });
  const [voidRec, setVoidRec] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // Form recibo
  const emptyForm = {
    date: todayLocalISO(),
    description: '',
    payment_details: [],
    withholdings: [],
    allocations: [], // {invoice_id, amount}
  };
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    if (!insurer?.insurer_id) return;
    try {
      const qs = [];
      if (ccFrom) qs.push(`from_date=${ccFrom}`);
      if (ccTo) qs.push(`to_date=${ccTo}`);
      const ccQs = qs.length ? `?${qs.join('&')}` : '';
      const [c, p, r, w, cb, pt, ic] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}/cc${ccQs}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}/pending-invoices`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}/receipts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/withholding-types/active`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/cash-banks/active`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/payment-types/active`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/treasury/income-concepts/active`, { withCredentials: true }),
      ]);
      setCc(c.data); setPending(p.data); setReceipts(r.data);
      setWhtTypes(w.data); setCashBanks(cb.data); setPaymentTypes(pt.data); setConcepts(ic.data);
    } catch (e) { toast.error('Error al cargar CC OS'); }
  };

  const openInvoicePdf = (iid) => {
    if (!iid) { toast.error('Sin factura vinculada'); return; }
    window.open(`${BACKEND_URL}/api/invoices/${iid}/pdf`, '_blank');
  };
  const openReceiptPdf = (rid) => {
    if (!rid) { toast.error('Sin recibo vinculado'); return; }
    window.open(`${BACKEND_URL}/api/insurer-receipts/${rid}/pdf`, '_blank');
  };
  const pdfUrlFor = (m) => {
    if (!m?.ref_id) return null;
    if (['invoice', 'nd', 'nc', 'advance_apply'].includes(m.kind)) return `${BACKEND_URL}/api/invoices/${m.ref_id}/pdf`;
    if (m.kind === 'payment') return `${BACKEND_URL}/api/insurer-receipts/${m.ref_id}/pdf`;
    return null;  // 'withholding' → externo (no PDF propio)
  };
  const pdfTitleFor = (m) => {
    if (m.kind === 'invoice') return 'Ver factura (PDF)';
    if (m.kind === 'nd') return 'Ver nota de débito (PDF)';
    if (m.kind === 'nc') return 'Ver nota de crédito (PDF)';
    if (m.kind === 'payment') return 'Ver recibo (PDF)';
    if (m.kind === 'advance_apply') return 'Ver factura imputada (PDF)';
    return '';
  };
  const exportPdf = () => {
    const qs = [];
    if (ccFrom) qs.push(`from_date=${ccFrom}`);
    if (ccTo) qs.push(`to_date=${ccTo}`);
    window.open(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}/account-statement/pdf${qs.length ? '?' + qs.join('&') : ''}`, '_blank');
  };
  const exportExcel = () => {
    const qs = [];
    if (ccFrom) qs.push(`from_date=${ccFrom}`);
    if (ccTo) qs.push(`to_date=${ccTo}`);
    window.open(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}/account-statement/excel${qs.length ? '?' + qs.join('&') : ''}`, '_blank');
  };

  const openAdvanceDialog = () => {
    const first = pending[0];
    const avail = Number(cc?.available_advance || 0);
    setAdvDialog({ open: true, invoice_id: first?.invoice_id || '', amount: first ? String(Math.min(first.outstanding || 0, avail)) : '', saving: false });
  };
  const onAdvInvoice = (iid) => {
    const fc = pending.find(x => x.invoice_id === iid);
    const avail = Number(cc?.available_advance || 0);
    const out = fc ? (fc.outstanding || 0) : 0;
    setAdvDialog(a => ({ ...a, invoice_id: iid, amount: String(Math.min(out, avail)) }));
  };
  const submitAdvance = async () => {
    if (!advDialog.invoice_id) { toast.error('Elegí una factura'); return; }
    const amt = Number(advDialog.amount);
    if (!amt || amt <= 0) { toast.error('Monto inválido'); return; }
    setAdvDialog(a => ({ ...a, saving: true }));
    try {
      const r = await axios.post(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}/apply-advance`,
        { invoice_id: advDialog.invoice_id, amount: amt }, { withCredentials: true });
      toast.success(`Anticipo aplicado: $${Number(r.data.applied).toLocaleString('es-AR')} a ${r.data.invoice_number}`);
      setAdvDialog({ open: false, invoice_id: '', amount: '', saving: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al aplicar anticipo');
      setAdvDialog(a => ({ ...a, saving: false }));
    }
  };

  const submitVoid = async () => {
    if ((voidReason || '').trim().length < 10) { toast.error('El motivo es obligatorio (mín. 10 caracteres)'); return; }
    setVoiding(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/insurer-receipts/${voidRec.receipt_id}/void`,
        { reason: voidReason.trim() }, { withCredentials: true });
      const revs = r.data?.reversal_entries || [];
      toast.success(`Recibo ${voidRec.receipt_number} anulado`
        + (revs.length ? ` · Contra-asiento #${revs.map(x => x.entry_number).join(', #')}` : ''));
      setVoidRec(null); setVoidReason(''); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo anular el recibo');
    } finally { setVoiding(false); }
  };

  useEffect(() => { if (open) { load(); setForm(emptyForm); setTab('cc'); } /* eslint-disable-next-line */ }, [open, insurer?.insurer_id]);
  useEffect(() => { if (open) { load(); } /* eslint-disable-next-line */ }, [ccFrom, ccTo]);

  const addPayment = () => setForm(f => ({ ...f, payment_details: [...f.payment_details, { payment_type_id: '', payment_type_name: '', amount: '', reference: '', cash_bank_id: '' }] }));
  const updPayment = (i, k, v) => setForm(f => ({ ...f, payment_details: f.payment_details.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const delPayment = (i) => setForm(f => ({ ...f, payment_details: f.payment_details.filter((_, idx) => idx !== i) }));
  const addWht = () => setForm(f => ({ ...f, withholdings: [...f.withholdings, { withholding_type_id: '', amount: '', certificate_number: '' }] }));
  const updWht = (i, k, v) => setForm(f => ({ ...f, withholdings: f.withholdings.map((w, idx) => idx === i ? { ...w, [k]: v } : w) }));
  const delWht = (i) => setForm(f => ({ ...f, withholdings: f.withholdings.filter((_, idx) => idx !== i) }));
  const togglePending = (inv) => setForm(f => {
    const exists = f.allocations.find(a => a.invoice_id === inv.invoice_id);
    if (exists) return { ...f, allocations: f.allocations.filter(a => a.invoice_id !== inv.invoice_id) };
    return { ...f, allocations: [...f.allocations, { invoice_id: inv.invoice_id, amount: inv.outstanding }] };
  });
  const updAllocAmount = (invId, amt) => setForm(f => ({ ...f, allocations: f.allocations.map(a => a.invoice_id === invId ? { ...a, amount: amt } : a) }));

  const cashTotal = form.payment_details.reduce((s, p) => s + Number(p.amount || 0), 0);
  const whtTotal = form.withholdings.reduce((s, w) => s + Number(w.amount || 0), 0);
  const grossTotal = cashTotal + whtTotal;
  const allocTotal = form.allocations.reduce((s, a) => s + Number(a.amount || 0), 0);
  const unallocated = grossTotal - allocTotal;

  const submitReceipt = async () => {
    if (cashTotal === 0 && whtTotal === 0) { toast.error('Cargá al menos un cobro o una retención'); return; }
    if (cashTotal > 0) {
      const missingBank = form.payment_details.find(p => Number(p.amount || 0) > 0 && !p.cash_bank_id);
      if (missingBank) { toast.error('Cada valor de pago debe tener su Caja/Banco asignada'); return; }
    }
    // v3.31.12 · Permitir recibo sin FCs seleccionadas (anticipo puro).
    // Si hay allocations, el imputado debe cuadrar con el bruto.
    if (form.allocations.length > 0 && Math.abs(unallocated) > 0.01) {
      toast.error(`El total bruto (${fmt(grossTotal)}) debe coincidir con el total imputado (${fmt(allocTotal)}) o dejar vacía la lista de FCs para registrar como anticipo`);
      return;
    }
    if (form.allocations.length === 0) {
      if (!window.confirm(`No marcaste ninguna FC. El total de ${fmt(grossTotal)} quedará como anticipo pendiente de aplicar. ¿Confirmás?`)) return;
    }

    setSaving(true);
    try {
      const pd = form.payment_details.map(p => {
        const pt = paymentTypes.find(x => x.payment_type_id === p.payment_type_id);
        return { payment_type_id: p.payment_type_id, payment_type_name: pt?.name || '',
                 amount: Number(p.amount), reference: p.reference || null,
                 cash_bank_id: p.cash_bank_id || null };
      });
      const wh = form.withholdings.map(w => ({ withholding_type_id: w.withholding_type_id,
                 amount: Number(w.amount), certificate_number: w.certificate_number || null }));
      const al = form.allocations.map(a => ({ invoice_id: a.invoice_id, amount: Number(a.amount) }));
      const r = await axios.post(`${BACKEND_URL}/api/health-insurers/${insurer.insurer_id}/receipt`, {
        date: form.date,
        description: form.description || null, payment_details: pd, withholdings: wh, allocations: al,
        auto_apply: false,  // v3.31.12 · nunca auto-imputar: anticipo puro queda como anticipo
      }, { withCredentials: true });
      toast.success(`Recibo ${r.data.receipt_number} emitido`);
      setForm(emptyForm);
      load();
      setTab('historial');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al emitir recibo'); }
    finally { setSaving(false); }
  };

  const downloadReceiptPdf = async (receipt) => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/insurer-receipts/${receipt.receipt_id}/pdf`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${receipt.receipt_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Error al descargar PDF'); }
  };

  if (!insurer) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="insurer-cc-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-indigo-600" />
            Cuenta Corriente · {insurer.name}
            {cc && (
              <Badge className={`ml-2 ${cc.balance > 0 ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>
                Saldo: {fmt(cc.balance)}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="cc" data-testid="cc-tab">Movimientos</TabsTrigger>
            <TabsTrigger value="recibo" data-testid="recibo-tab">Nuevo recibo</TabsTrigger>
            <TabsTrigger value="historial" data-testid="historial-tab">Historial</TabsTrigger>
          </TabsList>

          <TabsContent value="cc" className="space-y-3 pt-3">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Desde</Label>
                  <Input type="date" value={ccFrom} onChange={(e) => setCcFrom(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="insurer-cc-from" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Hasta</Label>
                  <Input type="date" value={ccTo} onChange={(e) => setCcTo(e.target.value)} className="h-8 w-[150px] text-xs" data-testid="insurer-cc-to" />
                </div>
                {(ccFrom || ccTo) && (
                  <Button size="sm" variant="ghost" onClick={() => { setCcFrom(''); setCcTo(''); }} className="h-8 text-xs" data-testid="insurer-cc-clear">Limpiar</Button>
                )}
                <label className="flex items-center gap-1 text-xs text-muted-foreground cursor-pointer select-none pb-1" data-testid="insurer-cc-show-all-wrap">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={showAllCc}
                    onChange={(e) => setShowAllCc(e.target.checked)}
                    data-testid="insurer-cc-show-all"
                  />
                  Ver cuenta corriente completa
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={exportPdf} data-testid="insurer-cc-export-pdf">
                  <Printer className="w-3.5 h-3.5 mr-1" />PDF
                </Button>
                <Button size="sm" variant="outline" onClick={exportExcel} data-testid="insurer-cc-export-xlsx">
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />Excel
                </Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              FCs pendientes: {pending.length} · Total adeudado: <b>{fmt(cc?.balance || 0)}</b>
              {!showAllCc && <span className="ml-1 text-emerald-700">· mostrando sólo pendientes</span>}
            </p>
            {Number(cc?.available_advance || 0) > 0 && (
              <div className="flex items-center justify-between rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2" data-testid="insurer-cc-advance">
                <div className="text-sm">Saldo a favor del convenio: <b className="text-emerald-700">{fmt(cc.available_advance)}</b></div>
                <Button size="sm" variant="outline" onClick={openAdvanceDialog} disabled={pending.length === 0} data-testid="insurer-apply-advance-btn">
                  <HandCoins className="w-4 h-4 mr-1" />Aplicar a factura
                </Button>
              </div>
            )}
            <Table>
              <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Tipo</TableHead><TableHead>Ref.</TableHead><TableHead>Descripción</TableHead><TableHead className="text-right">Debe</TableHead><TableHead className="text-right">Haber</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
              <TableBody>
                {(cc?.movements || []).length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-4">Sin movimientos</TableCell></TableRow>
                ) : (() => {
                  const visible = (cc.movements || []).filter(m => showAllCc ? true : m.has_pending !== false);
                  if (visible.length === 0) {
                    return <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-4">No hay comprobantes pendientes · activá "Ver cuenta corriente completa" para ver todo</TableCell></TableRow>;
                  }
                  return visible.map(m => (
                  <TableRow key={m.cc_id} data-testid={`cc-mov-${m.cc_id}`}>
                    <TableCell className="text-xs">{fmtDate(m.date)}</TableCell>
                    <TableCell>
                      {m.kind === 'invoice' && <Badge variant="outline" className="border-red-300 text-red-700">FC</Badge>}
                      {m.kind === 'nd' && <Badge variant="outline" className="border-rose-300 text-rose-700">ND</Badge>}
                      {m.kind === 'payment' && <Badge variant="outline" className="border-emerald-300 text-emerald-700">Cobro</Badge>}
                      {m.kind === 'withholding' && <Badge variant="outline" className="border-violet-300 text-violet-700">Retención</Badge>}
                      {m.kind === 'nc' && <Badge variant="outline" className="border-orange-300 text-orange-700">NC</Badge>}
                      {!['invoice','nd','payment','withholding','nc'].includes(m.kind) && <Badge variant="outline">{m.kind}</Badge>}
                    </TableCell>
                    <TableCell className="font-mono text-[11px]">
                      {m.ref_number}
                      {m.kind === 'payment' && (m.treasury_collection_numbers || []).length > 0 && (
                        <span className="block text-[10px] text-muted-foreground" data-testid={`cc-mov-cob-${m.cc_id}`}>
                          {m.treasury_collection_numbers.join(', ')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{m.description}</TableCell>
                    <TableCell className="text-right text-xs">{m.debit ? fmt(m.debit) : ''}</TableCell>
                    <TableCell className="text-right text-xs">{m.credit ? fmt(m.credit) : ''}</TableCell>
                    <TableCell className="text-right text-xs font-medium">{fmt(m.balance)}</TableCell>
                    <TableCell>
                      {pdfUrlFor(m) ? (
                        <button
                          type="button"
                          onClick={() => window.open(pdfUrlFor(m), '_blank')}
                          className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-muted text-blue-600 hover:text-blue-800 transition-colors"
                          title={pdfTitleFor(m)}
                          data-testid={`view-doc-${m.kind}-${m.cc_id}`}
                        >
                          <FileText className="w-4 h-4" />
                        </button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ));
                })()}
              </TableBody>
            </Table>
          </TabsContent>

          <TabsContent value="recibo" className="space-y-4 pt-3">
            {/* Encabezado */}
            <Card><CardContent className="pt-4 grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid="receipt-date" /></div>
              <div className="col-span-2"><Label className="text-xs">Descripción (opcional)</Label>
                <Input placeholder="Detalle del cobro..." value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} data-testid="receipt-description" /></div>
            </CardContent></Card>

            {/* Medios de pago */}
            <Card><CardContent className="pt-4 space-y-2">
              <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">💵 Valores de pago (efectivo, transferencia, cheque)</h3>
                <Button size="sm" variant="outline" onClick={addPayment} data-testid="add-payment-btn"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
              </div>
              {form.payment_details.length === 0 && <p className="text-xs text-muted-foreground">Sin pagos cargados</p>}
              {form.payment_details.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <div className="col-span-3">
                    {(() => {
                      const cb = cashBanks.find(x => x.cash_bank_id === p.cash_bank_id);
                      const cats = cb?.allowed_payment_categories ?? null;
                      const availPT = (paymentTypes || []).filter(t => {
                        if (cats === null) return true;
                        if (cats.length === 0) return false;
                        return cats.includes((t.category || '').toLowerCase());
                      });
                      return (
                        <select value={p.payment_type_id} onChange={(e) => updPayment(i, 'payment_type_id', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm">
                          <option value="">Tipo...</option>{availPT.map(t => <option key={t.payment_type_id} value={t.payment_type_id}>{t.name}</option>)}
                        </select>
                      );
                    })()}
                  </div>
                  <div className="col-span-2"><MoneyInput placeholder="$ 0,00" value={p.amount} onChange={(e) => updPayment(i, 'amount', e.target.value)} data-testid={`payment-amount-${i}`} /></div>
                  <div className="col-span-3">
                    <select value={p.cash_bank_id || ''} onChange={(e) => updPayment(i, 'cash_bank_id', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid={`payment-cashbank-${i}`}>
                      <option value="">Caja/Banco...</option>
                      {cashBanks.map(b => <option key={b.cash_bank_id} value={b.cash_bank_id}>{b.name}</option>)}
                    </select></div>
                  <div className="col-span-3"><Input placeholder="Referencia (nº cheque, transf...)" value={p.reference} onChange={(e) => updPayment(i, 'reference', e.target.value)} /></div>
                  <div className="col-span-1"><Button size="icon" variant="ghost" className="text-red-600" onClick={() => delPayment(i)}><Trash2 className="w-4 h-4" /></Button></div>
                </div>
              ))}
              <p className="text-xs text-right">Subtotal pago: <b>{fmt(cashTotal)}</b></p>
            </CardContent></Card>

            {/* Retenciones */}
            <Card><CardContent className="pt-4 space-y-2">
              <div className="flex items-center justify-between"><h3 className="text-sm font-semibold">📋 Retenciones de impuestos (sólo OS)</h3>
                <Button size="sm" variant="outline" onClick={addWht} data-testid="add-wht-btn"><Plus className="w-3 h-3 mr-1" />Agregar</Button>
              </div>
              {whtTypes.length === 0 && <p className="text-xs text-amber-700">⚠️ No hay tipos de retención cargados. Andá a Tesorería → Catálogos.</p>}
              {form.withholdings.length === 0 && <p className="text-xs text-muted-foreground">Sin retenciones</p>}
              {form.withholdings.map((w, i) => (
                <div key={i} className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <select value={w.withholding_type_id} onChange={(e) => updWht(i, 'withholding_type_id', e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid={`wht-type-${i}`}>
                      <option value="">Retención...</option>{whtTypes.map(t => <option key={t.withholding_type_id} value={t.withholding_type_id}>{t.code} · {t.name}</option>)}
                    </select></div>
                  <div className="col-span-3"><MoneyInput placeholder="$ 0,00" value={w.amount} onChange={(e) => updWht(i, 'amount', e.target.value)} data-testid={`wht-amount-${i}`} /></div>
                  <div className="col-span-4"><Input placeholder="N° certificado" value={w.certificate_number} onChange={(e) => updWht(i, 'certificate_number', e.target.value)} /></div>
                  <div className="col-span-1"><Button size="icon" variant="ghost" className="text-red-600" onClick={() => delWht(i)}><Trash2 className="w-4 h-4" /></Button></div>
                </div>
              ))}
              <p className="text-xs text-right">Subtotal retenciones: <b>{fmt(whtTotal)}</b></p>
            </CardContent></Card>

            {/* Imputación a FCs */}
            <Card><CardContent className="pt-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">🎯 Cancelar facturas pendientes</h3>
                <div className="text-xs">Total bruto: <b>{fmt(grossTotal)}</b> · Imputado: <b>{fmt(allocTotal)}</b> · {form.allocations.length === 0 ? <Badge className="bg-indigo-100 text-indigo-800">Anticipo puro: {fmt(grossTotal)}</Badge> : (Math.abs(unallocated) <= 0.01 ? <Badge className="bg-emerald-100 text-emerald-800">Calza</Badge> : <Badge className="bg-amber-100 text-amber-800">Diff: {fmt(unallocated)}</Badge>)}</div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Comprobante</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="text-right">A imputar</TableHead></TableRow></TableHeader>
                <TableBody>
                  {pending.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">No hay FCs pendientes</TableCell></TableRow>
                  ) : pending.map(inv => {
                    const alloc = form.allocations.find(a => a.invoice_id === inv.invoice_id);
                    return (
                      <TableRow key={inv.invoice_id} data-testid={`pending-${inv.invoice_id}`}>
                        <TableCell><input type="checkbox" checked={!!alloc} onChange={() => togglePending(inv)} data-testid={`alloc-check-${inv.invoice_id}`} /></TableCell>
                        <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                        <TableCell className="text-xs">{inv.issue_date}</TableCell>
                        <TableCell className="text-right text-xs">{fmt(inv.outstanding)}</TableCell>
                        <TableCell className="text-right">
                          {alloc ? <MoneyInput value={alloc.amount} onChange={(e) => updAllocAmount(inv.invoice_id, e.target.value)} className="h-8 w-32 text-right inline-block" /> : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent></Card>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
              <Button onClick={submitReceipt} disabled={saving || grossTotal <= 0 || (form.allocations.length > 0 && Math.abs(unallocated) > 0.01)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-receipt-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{form.allocations.length === 0 ? `Emitir anticipo por ${fmt(grossTotal)}` : `Emitir recibo por ${fmt(grossTotal)}`}
              </Button>
            </DialogFooter>
          </TabsContent>

          <TabsContent value="historial" className="space-y-3 pt-3">
            <Table>
              <TableHeader><TableRow><TableHead>Recibo</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Cobro</TableHead><TableHead className="text-right">Retenciones</TableHead><TableHead className="text-right">Bruto</TableHead><TableHead>Caja (COB)</TableHead><TableHead>FCs imputadas</TableHead><TableHead className="text-right w-16">PDF</TableHead><TableHead className="text-right w-16">Anular</TableHead></TableRow></TableHeader>
              <TableBody>
                {receipts.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-4">Sin recibos emitidos</TableCell></TableRow>
                ) : receipts.map(r => (
                  <TableRow key={r.receipt_id} data-testid={`receipt-row-${r.receipt_id}`} className={r.deleted_at ? 'opacity-60' : ''}>
                    <TableCell className="font-mono text-xs">
                      {r.receipt_number}
                      {r.deleted_at && <span className="ml-2 rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-medium text-white" data-testid={`receipt-voided-${r.receipt_id}`}>ANULADO</span>}
                    </TableCell>
                    <TableCell className="text-xs">{r.date}</TableCell>
                    <TableCell className={`text-right text-xs ${r.deleted_at ? 'line-through text-muted-foreground' : ''}`}>{fmt(r.cash_total)}</TableCell>
                    <TableCell className={`text-right text-xs ${r.deleted_at ? 'line-through text-muted-foreground' : ''}`}>{fmt(r.withholding_total)}</TableCell>
                    <TableCell className={`text-right text-xs font-semibold ${r.deleted_at ? 'line-through text-muted-foreground' : ''}`}>{fmt(r.gross_total)}</TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">{(r.treasury_collection_numbers || (r.treasury_collection_number ? [r.treasury_collection_number] : [])).join(', ') || '—'}</TableCell>
                    <TableCell className="text-xs">{(r.allocations || []).map(a => a.invoice_number).join(', ')}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => downloadReceiptPdf(r)} title="Descargar PDF" data-testid={`receipt-pdf-${r.receipt_id}`}>
                        <FileText className="w-4 h-4 text-indigo-600" />
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      {!r.deleted_at && (
                        <Button variant="ghost" size="icon" className="text-rose-600 hover:text-rose-700"
                          onClick={() => { setVoidRec(r); setVoidReason(''); }} title="Anular recibo (reversa todo)"
                          data-testid={`receipt-void-${r.receipt_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Dialog: Aplicar saldo a favor a factura */}
      <Dialog open={advDialog.open} onOpenChange={(o) => !o && setAdvDialog({ open: false, invoice_id: '', amount: '', saving: false })}>
        <DialogContent className="max-w-md" data-testid="insurer-advance-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-emerald-600" />Aplicar saldo a favor del convenio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border p-2 text-sm bg-emerald-50">Saldo a favor disponible: <b className="text-emerald-700">{fmt(cc?.available_advance || 0)}</b></div>
            <div><Label className="text-xs">Factura pendiente *</Label>
              <select value={advDialog.invoice_id} onChange={(e) => onAdvInvoice(e.target.value)} className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="insurer-advance-invoice">
                <option value="">Seleccionar...</option>
                {pending.map(f => <option key={f.invoice_id} value={f.invoice_id}>{f.invoice_number} — pendiente {fmt(f.outstanding || 0)}</option>)}
              </select></div>
            <div><Label className="text-xs">Monto a aplicar *</Label>
              <MoneyInput value={advDialog.amount} onChange={(e) => setAdvDialog(a => ({ ...a, amount: e.target.value }))} data-testid="insurer-advance-amount" />
            </div>
            <p className="text-[11px] text-muted-foreground">No mueve dinero: solo imputa el saldo a favor del convenio a la factura elegida.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvDialog({ open: false, invoice_id: '', amount: '', saving: false })}>Cancelar</Button>
            <Button onClick={submitAdvance} disabled={advDialog.saving} data-testid="insurer-advance-submit">{advDialog.saving ? 'Aplicando...' : 'Aplicar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Anular recibo de convenio (reversa total) */}
      <Dialog open={!!voidRec} onOpenChange={(o) => !o && (setVoidRec(null), setVoidReason(''))}>
        <DialogContent className="max-w-md" data-testid="insurer-void-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash2 className="w-5 h-5 text-rose-600" />Anular recibo {voidRec?.receipt_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800">
              Se revertirá TODO: contabilidad, cuenta corriente del convenio, saldo de las FC imputadas y la cobranza espejo{(voidRec?.treasury_collection_numbers || []).length ? ` (${(voidRec?.treasury_collection_numbers || []).join(', ')})` : ''}. Esta acción queda auditada.
            </div>
            <div>
              <Label className="text-xs">Motivo de la anulación * (mín. 10 caracteres)</Label>
              <textarea rows={3} value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                className="flex w-full rounded-md border px-2 py-1 text-sm" placeholder="Detallá el motivo..."
                data-testid="insurer-void-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setVoidRec(null); setVoidReason(''); }} disabled={voiding}>Cancelar</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={submitVoid} disabled={voiding} data-testid="insurer-void-submit">
              {voiding ? 'Anulando...' : 'Anular recibo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default InsurerCCDialog;
